import { computed, reactive, ref } from 'vue';

import {
  normalizeLanguageCode,
  normalizeResponseLengthCode,
} from './configurationValues';
import {
  captureAgentFormState,
  dirtyAgentFormGroups,
  dirtyAgentSections,
} from './formState';

const toArray = value => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const listToText = value => toArray(value).join(', ');
const textToList = value =>
  String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
const rulesToText = value =>
  toArray(value)
    .map(rule => String(rule || '').trim())
    .filter(Boolean)
    .join('\n');
const textToRules = value =>
  String(value || '')
    .split(/\r?\n/)
    .map(rule => rule.trim())
    .filter(Boolean);

const faqGroupListToText = value =>
  toArray(value)
    .map(group => {
      if (typeof group === 'string') return group.trim();
      const jid = group?.jid || group?.group_jid || '';
      const name = group?.name || group?.subject || '';
      return jid && name ? `${jid}|${name}` : jid;
    })
    .filter(Boolean)
    .join('\n');

const normalizeFaqLearningGroup = group => {
  if (typeof group === 'string') return { jid: group.trim(), name: '' };
  return {
    jid: String(group?.jid || group?.group_jid || group?.id || '').trim(),
    name: String(group?.name || group?.subject || group?.label || '').trim(),
  };
};

export const parseFaqLearningSourceGroups = value => {
  const groupsByJid = new Map();
  String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .forEach(line => {
      const separatorIndex = line.indexOf('|');
      const jid = line.slice(0, separatorIndex).trim();
      const name = line.slice(separatorIndex + 1).trim();
      if (separatorIndex > 0 && jid && name) {
        groupsByJid.set(jid.toLowerCase(), { jid, name });
      }
    });
  return Array.from(groupsByJid.values());
};

export const faqLearningSourceGroupLines = value =>
  String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

export const useAgentConfigurationForm = ({ assistant, management, t }) => {
  const identityForm = reactive({
    name: '',
    description: '',
    productName: '',
    persona: '',
    language: 'pt-BR',
    responseLength: 'short',
    unknownResponse: t('CAPTAIN_EXPERIENCE.SCRIPT.DEFAULT_UNKNOWN_RESPONSE'),
    responseGuidelinesText: '',
    guardrailsText: '',
    resolutionMessage: '',
    handoffMessage: '',
    businessHoursHandoffMessage: '',
    afterHoursHandoffMessage: '',
    featureFaq: false,
    featureMemory: false,
    featureCitation: false,
    featureContactAttributes: false,
    temperature: 0.5,
    qualificationMode: 'pre_attendance',
    preSchedulingLinkUrl: '',
    operationScope: 'both',
    allowedChannels: [],
    publicationMode: 'draft',
    rolloutPercent: 0,
    allowedOriginsText: '',
    audienceScopeRulesText: '',
    directWhatsappBehavior: 'respect_scope_with_notice',
    websiteDirectLinkPolicy: 'in_scope_only',
    fallbackTeamId: '',
    outOfHoursBehavior: 'coletar_e_handoff',
    faqLearningSourceGroupsText: '',
    faqLearningQuestionsGroupJid: '',
  });
  const memoryForm = reactive({ memoryType: 'fact', key: '', content: '' });
  const identityErrors = reactive({ name: '', productName: '' });
  const publicationErrors = reactive({
    preSchedulingLinkUrl: '',
    rolloutPercent: '',
  });
  const baselineFormState = ref({});

  const faqLearningManagement = computed(() => {
    const currentManagement = management.value || {};
    const nestedConfiguration =
      currentManagement.faq_learning &&
      typeof currentManagement.faq_learning === 'object'
        ? currentManagement.faq_learning
        : {};
    const sourceGroups =
      nestedConfiguration.source_groups ||
      currentManagement.faq_learning_source_groups ||
      [];
    const availableSourceGroups =
      nestedConfiguration.available_source_groups ||
      nestedConfiguration.allowed_source_groups ||
      currentManagement.allowed_faq_learning_source_groups ||
      sourceGroups;
    const questionsGroup =
      nestedConfiguration.required_questions_group_jid ||
      nestedConfiguration.questions_group_jid ||
      nestedConfiguration.questions_group ||
      nestedConfiguration.questions_destination ||
      currentManagement.faq_learning_questions_group ||
      currentManagement.faq_learning_questions_group_jid ||
      null;
    return {
      sourceGroups: toArray(sourceGroups)
        .map(normalizeFaqLearningGroup)
        .filter(group => group.jid),
      availableSourceGroups: toArray(availableSourceGroups)
        .map(normalizeFaqLearningGroup)
        .filter(group => group.jid),
      questionsGroup: normalizeFaqLearningGroup(questionsGroup),
      enabled: nestedConfiguration.enabled !== false,
      locked: Boolean(nestedConfiguration.locked),
    };
  });

  const faqLearningAuthorizedGroups = computed(
    () =>
      new Map(
        faqLearningManagement.value.availableSourceGroups.map(group => [
          group.jid.toLowerCase(),
          group.name,
        ])
      )
  );
  const selectedFaqLearningSourceGroupIds = computed(
    () =>
      new Set(
        parseFaqLearningSourceGroups(
          identityForm.faqLearningSourceGroupsText
        ).map(group => group.jid.toLowerCase())
      )
  );
  const currentFormState = computed(() =>
    captureAgentFormState(identityForm, memoryForm)
  );
  const dirtyFormGroups = computed(() =>
    dirtyAgentFormGroups(baselineFormState.value, currentFormState.value)
  );
  const dirtySectionState = computed(() =>
    dirtyAgentSections(dirtyFormGroups.value)
  );
  const hasUnsavedChanges = computed(() =>
    Object.values(dirtyFormGroups.value).some(Boolean)
  );

  const updateField = (form, { field, value }) => {
    if (typeof field === 'string') form[field] = value;
  };
  const updateIdentityField = payload => {
    updateField(identityForm, payload);
    if (Object.hasOwn(identityErrors, payload.field)) {
      identityErrors[payload.field] = '';
    }
    if (Object.hasOwn(publicationErrors, payload.field)) {
      publicationErrors[payload.field] = '';
    }
  };
  const updateMemoryField = payload => updateField(memoryForm, payload);
  const toggleFaqGroup = ({ group, selected }) => {
    const selectedGroups = new Map(
      parseFaqLearningSourceGroups(
        identityForm.faqLearningSourceGroupsText
      ).map(currentGroup => [currentGroup.jid.toLowerCase(), currentGroup])
    );
    if (selected) selectedGroups.set(group.jid.toLowerCase(), group);
    else selectedGroups.delete(group.jid.toLowerCase());
    identityForm.faqLearningSourceGroupsText = faqGroupListToText(
      Array.from(selectedGroups.values())
    );
  };

  const resetValidationErrors = () => {
    Object.keys(identityErrors).forEach(key => {
      identityErrors[key] = '';
    });
    Object.keys(publicationErrors).forEach(key => {
      publicationErrors[key] = '';
    });
  };
  const markFormGroupsSaved = groups => {
    const snapshots = captureAgentFormState(identityForm, memoryForm);
    baselineFormState.value = {
      ...baselineFormState.value,
      ...Object.fromEntries(groups.map(group => [group, snapshots[group]])),
    };
  };
  const resetMemoryForm = ({ markSaved = true } = {}) => {
    Object.assign(memoryForm, { memoryType: 'fact', key: '', content: '' });
    if (markSaved) markFormGroupsSaved(['memoryDraft']);
  };

  const syncIdentityForm = () => {
    const currentAssistant = assistant.value || {};
    const config = currentAssistant.config || {};
    Object.assign(identityForm, {
      name: currentAssistant.name || '',
      description: currentAssistant.description || '',
      productName: config.product_name || '',
      persona: config.persona || '',
      language: normalizeLanguageCode(config.language || 'pt-BR'),
      responseLength: normalizeResponseLengthCode(
        config.response_length || 'short'
      ),
      unknownResponse:
        config.unknown_response ||
        t('CAPTAIN_EXPERIENCE.SCRIPT.DEFAULT_UNKNOWN_RESPONSE'),
      responseGuidelinesText: rulesToText(currentAssistant.response_guidelines),
      guardrailsText: rulesToText(currentAssistant.guardrails),
      resolutionMessage: config.resolution_message || '',
      handoffMessage:
        config.handoff_message ||
        t('CAPTAIN_EXPERIENCE.SCRIPT.DEFAULT_HANDOFF_MESSAGE'),
      businessHoursHandoffMessage:
        config.business_hours_handoff_message ||
        config.handoff_message ||
        t('CAPTAIN_EXPERIENCE.SCRIPT.DEFAULT_HANDOFF_MESSAGE'),
      afterHoursHandoffMessage:
        config.after_hours_handoff_message ||
        t('CAPTAIN_EXPERIENCE.SCRIPT.DEFAULT_AFTER_HOURS_HANDOFF_MESSAGE'),
      featureFaq: config.feature_faq ?? false,
      featureMemory: config.feature_memory ?? false,
      featureCitation: config.feature_citation ?? false,
      featureContactAttributes: config.feature_contact_attributes ?? false,
      temperature: Number(config.temperature ?? 0.5),
      qualificationMode: config.qualification_mode || 'pre_attendance',
      preSchedulingLinkUrl: config.pre_scheduling_link_url || '',
      operationScope: config.operation_scope || 'both',
      allowedChannels: toArray(config.allowed_channels),
      publicationMode: config.publication_mode || 'draft',
      rolloutPercent: Number(
        config.lead_rollout_percent ?? config.rollout_percent ?? 0
      ),
      faqLearningSourceGroupsText: faqGroupListToText(
        config.faq_learning_source_groups ??
          faqLearningManagement.value.sourceGroups
      ),
      faqLearningQuestionsGroupJid:
        config.faq_learning_questions_group_jid ||
        faqLearningManagement.value.questionsGroup.jid,
      allowedOriginsText: listToText(config.allowed_origins),
      audienceScopeRulesText: listToText(config.audience_scope_rules),
      directWhatsappBehavior:
        config.direct_whatsapp_behavior || 'respect_scope_with_notice',
      websiteDirectLinkPolicy:
        config.website_direct_link_policy || 'in_scope_only',
      fallbackTeamId: config.fallback_team_id
        ? Number(config.fallback_team_id)
        : '',
      outOfHoursBehavior: config.out_of_hours_behavior || 'coletar_e_handoff',
    });
    resetValidationErrors();
    baselineFormState.value = captureAgentFormState(identityForm, memoryForm);
  };

  const buildPublicationSettings = () => ({
    qualification_mode: identityForm.qualificationMode,
    pre_scheduling_link_url: identityForm.preSchedulingLinkUrl,
    operation_scope: identityForm.operationScope,
    allowed_channels: identityForm.allowedChannels,
    allowed_origins: textToList(identityForm.allowedOriginsText),
    audience_scope_rules: textToList(identityForm.audienceScopeRulesText),
    direct_whatsapp_behavior: identityForm.directWhatsappBehavior,
    website_direct_link_policy: identityForm.websiteDirectLinkPolicy,
    fallback_team_id: identityForm.fallbackTeamId
      ? Number(identityForm.fallbackTeamId)
      : '',
    out_of_hours_behavior: identityForm.outOfHoursBehavior,
  });

  const builders = {
    identity: () => ({
      product_name: identityForm.productName,
      persona: identityForm.persona,
      language: identityForm.language,
      response_length: identityForm.responseLength,
      unknown_response: identityForm.unknownResponse,
      resolution_message: identityForm.resolutionMessage,
      feature_citation: identityForm.featureCitation,
      feature_contact_attributes: identityForm.featureContactAttributes,
      temperature: Number(identityForm.temperature),
    }),
    behavior: () => ({
      response_guidelines: textToRules(identityForm.responseGuidelinesText),
      guardrails: textToRules(identityForm.guardrailsText),
    }),
    knowledge: () => ({
      feature_faq: identityForm.featureFaq,
      feature_memory: identityForm.featureMemory,
    }),
    handoff: () => ({
      handoff_message: identityForm.handoffMessage,
      business_hours_handoff_message: identityForm.businessHoursHandoffMessage,
      after_hours_handoff_message: identityForm.afterHoursHandoffMessage,
    }),
    publicationSettings: buildPublicationSettings,
  };

  return {
    identityForm,
    memoryForm,
    identityErrors,
    publicationErrors,
    baselineFormState,
    faqLearningManagement,
    faqLearningAuthorizedGroups,
    selectedFaqLearningSourceGroupIds,
    dirtyFormGroups,
    dirtySectionState,
    hasUnsavedChanges,
    updateIdentityField,
    updateMemoryField,
    toggleFaqGroup,
    markFormGroupsSaved,
    resetMemoryForm,
    syncIdentityForm,
    builders,
    faqGroupListToText,
  };
};
